import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  DataGridPro,
  GridColDef,
  GridComparatorFn,
  GridFilterModel,
  GridOverlay,
  GridRowId,
  GridSortModel,
  gridVisibleSortedRowIdsSelector,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { GridFilterInputValue, GridFilterInputValueProps } from '@mui/x-data-grid';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import moment from 'moment';

import Layout from '../../Layout/Layout';
import { useAppSelector } from '../../../app/hooks';
import { updateAssessorAnswer } from '../../../services/assessments';
import { createComment, deleteComment, updateComment } from '../../../services/commentsLogs';
import { getQCReviewData, getQCReviews, updateQCReviewAdminResponse } from '../../../services/qcReview';
import { AssessorAnswer } from '../../../interfaces/assessment.interface';
import {
  QCAdminResponse,
  QCReview,
  QCVerdict,
  VerificationStatus,
  VERIFICATION_STATUSES,
  VERIFICATION_STATUS_LABELS,
} from '../../../interfaces/qcReview.interface';
import { QCReviewItem } from '../../../interfaces/qcReviewData.interface';
import ConsolidatedReviewView from './ConsolidatedReviewView';
import {
  QCAttention,
  QCAttentionRow,
  compareAttention,
  compareMinConfidence,
  compareVerdictSeverity,
  deriveRow,
} from './qcAttention';
import BaseToolbar from '../../Datagrid/BaseToolbar';
import SelectFilter, { SelectFilterOption } from '../SelectFilter';
import BooleanFilterComponent from '../BooleanFilter';

interface ReviewItem {
  item: QCReviewItem;
  qcReviews: QCReview[];
}

interface WorklistRow extends QCAttentionRow {
  questionTitle: string;
  questionShortCode: string | null;
  marking: string | null;
}

const ATTENTION_LABELS: Record<QCAttention, string> = {
  'needs-me': 'Needs Me',
  'awaiting-assessor': 'Awaiting Assessor',
  done: 'Done',
};

const ATTENTION_COLORS: Record<QCAttention, 'error' | 'warning' | 'success'> = {
  'needs-me': 'error',
  'awaiting-assessor': 'warning',
  done: 'success',
};

const VERDICT_LABELS: Record<QCVerdict, string> = {
  disagree: 'Disagree',
  uncertain: 'Uncertain',
  not_evaluated: 'Not Evaluated',
  agree: 'Agree',
};

const VERDICT_COLORS: Record<QCVerdict, 'default' | 'error' | 'warning'> = {
  disagree: 'error',
  uncertain: 'warning',
  not_evaluated: 'warning',
  agree: 'default',
};

const ATTENTION_OPTIONS: SelectFilterOption[] = [
  { value: '', label: 'All' },
  { value: 'needs-me', label: ATTENTION_LABELS['needs-me'] },
  { value: 'awaiting-assessor', label: ATTENTION_LABELS['awaiting-assessor'] },
  { value: 'done', label: ATTENTION_LABELS.done },
];

const VERDICT_OPTIONS: SelectFilterOption[] = [
  { value: '', label: 'All' },
  { value: 'disagree', label: VERDICT_LABELS.disagree },
  { value: 'uncertain', label: VERDICT_LABELS.uncertain },
  { value: 'not_evaluated', label: VERDICT_LABELS.not_evaluated },
  { value: 'agree', label: VERDICT_LABELS.agree },
];

// QC covers the whole assessment, not just NI/PI, so every marking is filterable here.
const MARKING_OPTIONS: SelectFilterOption[] = [
  { value: '', label: 'All' },
  { value: 'Not Implemented', label: 'Not Implemented' },
  { value: 'Partially Implemented', label: 'Partially Implemented' },
  { value: 'Fully Implemented', label: 'Fully Implemented' },
  { value: 'Not Applicable', label: 'Not Applicable' },
];

const VERIFICATION_OPTIONS: SelectFilterOption[] = [
  { value: '', label: 'All' },
  ...Object.values(VERIFICATION_STATUSES).map(status => ({
    value: status,
    label: VERIFICATION_STATUS_LABELS[status],
  })),
];

function AttentionFilterInput(props: GridFilterInputValueProps) {
  return <SelectFilter {...props} options={ATTENTION_OPTIONS} />;
}

function WorstVerdictFilterInput(props: GridFilterInputValueProps) {
  return <SelectFilter {...props} options={VERDICT_OPTIONS} />;
}

function MarkingFilterInput(props: GridFilterInputValueProps) {
  return <SelectFilter {...props} options={MARKING_OPTIONS} />;
}

function VerificationStatusFilterInput(props: GridFilterInputValueProps) {
  return <SelectFilter {...props} options={VERIFICATION_OPTIONS} />;
}

// Equality filter for the enum-valued columns above (attention/worstVerdict/marking/
// verificationStatus) - client-side, so unlike the server-driven `getApplyFilterFn: () => null`
// pattern elsewhere (e.g. AssessmentAdminPanel.tsx), this one has to actually filter in-browser.
function equalsFilterOperator(InputComponent: React.ComponentType<GridFilterInputValueProps>): GridColDef['filterOperators'] {
  return [{
    label: 'Is',
    value: 'is',
    getApplyFilterFn: (filterItem) => {
      if (filterItem.value === undefined || filterItem.value === null || filterItem.value === '') return null;
      return ({ value }) => value === filterItem.value;
    },
    InputComponent,
  }];
}

function booleanFilterOperator(): GridColDef['filterOperators'] {
  return [{
    label: 'Is',
    value: 'is',
    getApplyFilterFn: (filterItem) => ({ value }) => value === filterItem.value,
    InputComponent: BooleanFilterComponent,
  }];
}

// A real client-side "contains" operator. NOTE: adminDashboardUtils' `stringContainsOnly` is
// NOT reusable here - its getApplyFilterFn always returns null, which is only correct when
// filtering is delegated to the server (as in AssessmentAdminPanel.tsx); under this page's
// client filterMode that would silently no-op the filter.
function containsFilterOperator(): GridColDef['filterOperators'] {
  return [{
    label: 'Contains',
    value: 'contains',
    getApplyFilterFn: (filterItem) => {
      if (!filterItem.value) return null;
      const needle = String(filterItem.value).toLowerCase();
      return ({ value }) => String(value ?? '').toLowerCase().includes(needle);
    },
    InputComponent: GridFilterInputValue,
  }];
}

// Hide-done toggle lives in the toolbar, alongside AssessmentAdminPanel.tsx's
// showCompletedAssessmentSurveys toggle pattern - the same "own custom props out, spread the
// rest into BaseToolbar" shape.
function QCReviewToolbar(props: { hideDone: boolean; toggleHideDone: () => void }) {
  const { hideDone, toggleHideDone, ...rest } = props;
  return (
    <BaseToolbar {...rest}>
      <Button variant="text" size="small" onClick={toggleHideDone}>
        {hideDone ? 'Show Done' : 'Hide Done'}
      </Button>
    </BaseToolbar>
  );
}

const attentionComparator: GridComparatorFn<QCAttention> = (v1, v2) => compareAttention(v1, v2);
const verdictComparator: GridComparatorFn<QCVerdict> = (v1, v2) => compareVerdictSeverity(v1, v2);
const confidenceComparator: GridComparatorFn<number | null> = (v1, v2) => compareMinConfidence(v1, v2);

// Default sort mirrors the "Attention" priority signal (CONTEXT.md): most urgent rows first,
// then most-severe verdict, then lowest (least trustworthy) confidence.
const DEFAULT_SORT_MODEL: GridSortModel = [
  { field: 'attention', sort: 'asc' },
  { field: 'worstVerdict', sort: 'asc' },
  { field: 'minConfidence', sort: 'asc' },
];

export default function AssessmentQCReviewPage() {
  const { workflowId } = useParams<{ workflowId: string }>();
  const { user } = useAppSelector(state => state.user);
  const apiRef = useGridApiRef();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reviewItems, setReviewItems] = useState<ReviewItem[]>([]);
  const [selectedItemId, setSelectedItemId] = useState<number | null>(null);
  const [hideDone, setHideDone] = useState(false);

  const [sortModel, setSortModel] = useState<GridSortModel>(DEFAULT_SORT_MODEL);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });

  // `silent` skips the loading flag for background refreshes (e.g. after posting a comment).
  // The top-level render unmounts the whole grid + ConsolidatedReviewView tree while loading
  // is true, which would reset ConsolidatedReviewView's local showComments state back to
  // false on remount - the new comment would be in the refetched data, but its thread would
  // collapse shut in the same instant, making the save look like it silently failed.
  const loadData = useCallback(async (options?: { silent?: boolean }) => {
    if (!workflowId) return;
    if (!options?.silent) setLoading(true);
    setLoadError(null);
    try {
      const parsedWorkflowId = parseInt(workflowId, 10);
      const [items, qcReviews] = await Promise.all([
        getQCReviewData(parsedWorkflowId),
        getQCReviews(parsedWorkflowId),
      ]);

      setReviewItems(items.map(item => ({
        item,
        qcReviews: qcReviews.filter(qc => qc.assessorAnswerId === item.id),
      })));

      // Preserve the admin's place in the queue across a refetch (e.g. after posting a
      // comment) instead of always snapping back to the first row; only fall back to the
      // first item when there's no prior selection or it no longer exists.
      setSelectedItemId(prev => (prev !== null && items.some(i => i.id === prev) ? prev : items[0]?.id ?? null));
    } catch (error) {
      console.error('Error loading QC review data:', error);
      // Previously this had no catch, so a 500 fell through to the "no NI/PI questions found"
      // empty state - an outage looked identical to a clean assessment with nothing to review.
      setLoadError('Failed to load QC review data. Please try again.');
    } finally {
      if (!options?.silent) setLoading(false);
    }
  }, [workflowId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handlePostComment = useCallback(async (item: QCReviewItem, text: string, attachments: number[]) => {
    if (!user) return;
    const comment = await createComment({ user, text, attachments });
    const existingIds = item.comments.map(c => c.id);
    await updateAssessorAnswer({
      id: item.id,
      question: item.question.id,
      comments: [...existingIds, comment.id!],
    } as AssessorAnswer);
    // sortModel/filterModel/selectedItemId are untouched here and in loadData, so the grid's
    // sort, filters, and the admin's place in the queue all survive this refetch. Silent so
    // the comment thread stays open and shows the new comment instead of collapsing shut.
    await loadData({ silent: true });
  }, [user, loadData]);

  // Edit/delete both refetch silently for the same reason handlePostComment does: a
  // non-silent reload would remount ConsolidatedReviewView and snap the thread shut.
  // Deleting the Comment row drops the AssessorAnswer m2m link too, so no PATCH is needed.
  const handleEditComment = useCallback(async (commentId: number, text: string) => {
    await updateComment(commentId, text);
    await loadData({ silent: true });
  }, [loadData]);

  const handleDeleteComment = useCallback(async (commentId: number) => {
    await deleteComment(commentId);
    await loadData({ silent: true });
  }, [loadData]);

  // DISPOSITION control - writes AssessorAnswer.verification_status (ADR-0007). Optimistic:
  // updates local state immediately, then PATCHes; reverts on failure rather than forcing a
  // full-page reload for a single control change.
  const handleSetVerificationStatus = useCallback(async (item: QCReviewItem, status: VerificationStatus) => {
    const previousStatus = item.verificationStatus;
    setReviewItems(prev => prev.map(ri => (
      ri.item.id === item.id ? { ...ri, item: { ...ri.item, verificationStatus: status } } : ri
    )));
    try {
      await updateAssessorAnswer({
        id: item.id,
        question: item.question.id,
        verificationStatus: status,
      } as AssessorAnswer);
    } catch (error) {
      console.error('Error updating verification status:', error);
      setReviewItems(prev => prev.map(ri => (
        ri.item.id === item.id ? { ...ri, item: { ...ri.item, verificationStatus: previousStatus } } : ri
      )));
    }
  }, []);

  // ADMIN RESPONSE control - writes QCReview.admin_response, per verdict (ADR-0007). Also
  // optimistic, reverting to the prior response on failure.
  const handleSetAdminResponse = useCallback(async (qcReviewId: number, response: QCAdminResponse) => {
    const previousResponse = reviewItems
      .flatMap(ri => ri.qcReviews)
      .find(qc => qc.id === qcReviewId)?.adminResponse ?? null;

    setReviewItems(prev => prev.map(ri => ({
      ...ri,
      qcReviews: ri.qcReviews.map(qc => (
        qc.id === qcReviewId ? { ...qc, adminResponse: response, adminResponseAt: new Date().toISOString() } : qc
      )),
    })));

    try {
      await updateQCReviewAdminResponse(qcReviewId, response);
    } catch (error) {
      console.error('Error updating QC review admin response:', error);
      setReviewItems(prev => prev.map(ri => ({
        ...ri,
        qcReviews: ri.qcReviews.map(qc => (
          qc.id === qcReviewId ? { ...qc, adminResponse: previousResponse, adminResponseAt: null } : qc
        )),
      })));
    }
  }, [reviewItems]);

  const rows: WorklistRow[] = useMemo(() => reviewItems.map(({ item, qcReviews }) => ({
    ...deriveRow(item, qcReviews),
    questionTitle: item.question.title,
    questionShortCode: item.question.shortCode,
    marking: item.answer,
  })), [reviewItems]);

  const visibleRows = useMemo(
    () => (hideDone ? rows.filter(row => row.attention !== 'done') : rows),
    [rows, hideDone],
  );

  const columns: GridColDef<WorklistRow>[] = useMemo(() => [
    {
      field: 'attention',
      headerName: 'Attention',
      width: 160,
      sortComparator: attentionComparator,
      filterOperators: equalsFilterOperator(AttentionFilterInput),
      renderCell: (params) => (
        <Chip size="small" label={ATTENTION_LABELS[params.value as QCAttention]} color={ATTENTION_COLORS[params.value as QCAttention]} />
      ),
    },
    {
      field: 'questionShortCode',
      headerName: 'Code',
      width: 100,
      filterOperators: containsFilterOperator(),
      valueFormatter: ({ value }) => (value as string) || '—',
    },
    {
      field: 'questionTitle',
      headerName: 'Question',
      flex: 2,
      minWidth: 220,
      filterOperators: containsFilterOperator(),
    },
    {
      field: 'marking',
      headerName: 'Marking',
      width: 190,
      filterOperators: equalsFilterOperator(MarkingFilterInput),
      valueFormatter: ({ value }) => (value as string) || '—',
    },
    {
      field: 'flaggedCount',
      headerName: 'Flagged / Total',
      width: 140,
      type: 'number',
      renderCell: (params) => `${params.row.flaggedCount} / ${params.row.subControlCount}`,
    },
    {
      field: 'worstVerdict',
      headerName: 'Worst Verdict',
      width: 160,
      sortComparator: verdictComparator,
      filterOperators: equalsFilterOperator(WorstVerdictFilterInput),
      renderCell: (params) => (
        <Chip size="small" label={VERDICT_LABELS[params.value as QCVerdict]} color={VERDICT_COLORS[params.value as QCVerdict]} />
      ),
    },
    {
      field: 'errorClasses',
      headerName: 'Error Class',
      width: 220,
      filterOperators: containsFilterOperator(),
      valueGetter: (params) => (params.row.errorClasses || []).map(ec => ec.replace(/_/g, ' ')).join(', '),
    },
    {
      field: 'minConfidence',
      headerName: 'Min Confidence',
      width: 150,
      type: 'number',
      sortComparator: confidenceComparator,
      valueFormatter: ({ value }) => (value === null || value === undefined ? '—' : `${Math.round((value as number) * 100)}%`),
    },
    {
      field: 'suspectedInjection',
      headerName: 'Suspected Injection',
      width: 170,
      filterOperators: booleanFilterOperator(),
      renderCell: (params) => (params.value ? (
        <Tooltip title="Suspected prompt injection in evidence — treat as needs-human, not a pass">
          <Chip size="small" icon={<WarningAmberIcon />} label="Suspected" color="warning" />
        </Tooltip>
      ) : null),
    },
    {
      field: 'lastComment',
      headerName: 'Last Comment',
      width: 220,
      valueGetter: (params) => (params.row.lastCommentAt ? new Date(params.row.lastCommentAt) : null),
      renderCell: (params) => {
        const { lastCommentAt, lastCommentAuthorType } = params.row;
        if (!lastCommentAt) return '—';
        return `${lastCommentAuthorType ?? 'unknown'} · ${moment(lastCommentAt).format('MM/DD/YYYY h:mm A')}`;
      },
    },
    {
      field: 'commentCount',
      headerName: '# Comments',
      width: 130,
      type: 'number',
    },
    {
      field: 'spDivergence',
      headerName: 'SP Divergence',
      width: 140,
      filterOperators: booleanFilterOperator(),
      renderCell: (params) => (params.value ? <Chip size="small" label="Diverges" color="error" variant="outlined" /> : null),
    },
    {
      field: 'verificationStatus',
      headerName: 'Your Call',
      width: 170,
      filterOperators: equalsFilterOperator(VerificationStatusFilterInput),
      valueFormatter: ({ value }) => VERIFICATION_STATUS_LABELS[value as VerificationStatus],
    },
  ], []);

  const selectedReviewItem = reviewItems.find(ri => ri.item.id === selectedItemId);

  // Computed fresh every render (cheap memoized selector) so it always reflects the current
  // filtered + sorted view, rather than only updating on the next click.
  // Guard on apiRef.current.state, not just apiRef.current: the grid assigns apiRef.current as
  // a partial object before its state-init effect attaches .state, so a bare truthiness check
  // on apiRef.current can call the selector one render too early and crash (undefined state
  // reaching createSelector's internal apiRef-vs-state branch).
  const queueIds: GridRowId[] = apiRef.current?.state ? gridVisibleSortedRowIdsSelector(apiRef) : visibleRows.map(r => r.id);
  const currentIndex = selectedItemId !== null ? queueIds.indexOf(selectedItemId) : -1;

  const handlePrev = () => {
    if (currentIndex > 0) setSelectedItemId(queueIds[currentIndex - 1] as number);
  };
  const handleNext = () => {
    if (currentIndex >= 0 && currentIndex < queueIds.length - 1) setSelectedItemId(queueIds[currentIndex + 1] as number);
  };

  return (
    <Layout pageTitle="Assessment QC Review" link="/admin/assessments" buttonText="Back to Assessments">
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}
      {!loading && loadError && <Alert severity="error">{loadError}</Alert>}
      {!loading && !loadError && reviewItems.length === 0 && (
        <Typography>No questions found for this assessment.</Typography>
      )}
      {!loading && !loadError && reviewItems.length > 0 && (
        <>
          <Card sx={{ height: 560, width: '100%', mb: 3 }}>
            <DataGridPro
              apiRef={apiRef}
              rows={visibleRows}
              columns={columns}
              getRowId={(row) => row.id}
              onRowClick={(params) => setSelectedItemId(params.row.id as number)}
              // Client-side sort/filter deliberately, unlike the server-side pattern in
              // AssessmentAdminPanel.tsx: all ~150-200 rows for this assessment are already
              // loaded up front, so there's no page to round-trip. Do NOT "fix" this to
              // server mode — there's nothing on the server to page through.
              sortingMode="client"
              filterMode="client"
              sortModel={sortModel}
              onSortModelChange={setSortModel}
              filterModel={filterModel}
              onFilterModelChange={setFilterModel}
              getRowClassName={(params) => (params.id === selectedItemId ? 'qc-row-selected' : '')}
              sx={{
                // !important + borderLeft: DataGridPro's own row/hover backgrounds otherwise
                // win the specificity fight, making the previous action.selected overlay too
                // faint to notice - especially after Previous/Next moves selection without a
                // click (no hover state to reinforce it).
                '& .qc-row-selected, & .qc-row-selected:hover': {
                  backgroundColor: 'action.selected !important',
                  borderLeft: '3px solid',
                  borderLeftColor: 'primary.main',
                },
              }}
              components={{
                Toolbar: QCReviewToolbar,
                LoadingOverlay: () => (
                  <GridOverlay>
                    <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
                      <CircularProgress />
                    </div>
                  </GridOverlay>
                ),
              }}
              componentsProps={{
                toolbar: {
                  hideDone,
                  toggleHideDone: () => setHideDone(v => !v),
                  csvOptions: { fileName: `qc_review_${workflowId}_${new Date().toLocaleDateString()}` },
                },
              }}
            />
          </Card>

          {selectedReviewItem ? (
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="subtitle1">
                  {currentIndex >= 0 ? `${currentIndex + 1} of ${queueIds.length}` : ''}
                </Typography>
                <Stack direction="row" spacing={2}>
                  <Button
                    variant="outlined"
                    startIcon={<ChevronLeftIcon />}
                    disabled={currentIndex <= 0}
                    onClick={handlePrev}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outlined"
                    endIcon={<ChevronRightIcon />}
                    disabled={currentIndex === -1 || currentIndex >= queueIds.length - 1}
                    onClick={handleNext}
                  >
                    Next
                  </Button>
                </Stack>
              </Stack>
              <ConsolidatedReviewView
                key={selectedReviewItem.item.id}
                item={selectedReviewItem.item}
                qcReviews={selectedReviewItem.qcReviews}
                canEdit
                onPostComment={(text, attachments) => handlePostComment(selectedReviewItem.item, text, attachments)}
                onEditComment={handleEditComment}
                onDeleteComment={handleDeleteComment}
                onSetVerificationStatus={(status) => handleSetVerificationStatus(selectedReviewItem.item, status)}
                onSetAdminResponse={handleSetAdminResponse}
              />
            </Box>
          ) : (
            <Typography>Select a row above to view its details.</Typography>
          )}
        </>
      )}
    </Layout>
  );
}
